import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Loader2, RefreshCcw, AlertTriangle, TrendingDown } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { apiClient, extractErrorMessage } from '@/lib/api';
import { useAuth } from '@/lib/auth';

type Period = 'last7' | 'last30' | 'last90';

interface Performance {
  driverId: string;
  driverName: string | null;
  periodStart: string;
  periodEnd: string;
  totalTrips: number;
  totalKm: number;
  scoreConsumption: number | null;
  scoreIdle: number | null;
  scoreDrivingStyle: number | null;
  scoreSchedule: number | null;
  scoreMaintenance: number | null;
  compositeScore: number | null;
  wasteCostSar: number;
  evidence: Record<string, unknown>;
}

interface WorstTrip {
  id: string;
  code: string;
  trip_date: string;
  consumption_variance_pct: string | null;
  waste_cost_sar: string | null;
  consumption_status: string | null;
  vehicle_plate: string | null;
}

const fmtSar = (n: number) => 'SAR ' + n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (s: string) => new Date(s).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });

function tier(s: number | null) {
  if (s === null) return { label: 'No data', color: 'text-muted-foreground' };
  if (s >= 90) return { label: 'Excellent — bonus candidate',     color: 'text-emerald-600 dark:text-emerald-400' };
  if (s >= 75) return { label: 'Good — meeting standard',         color: 'text-sky-600 dark:text-sky-400' };
  if (s >= 60) return { label: 'Needs coaching',                  color: 'text-amber-600 dark:text-amber-400' };
  if (s >= 40) return { label: 'Serious problem — perf review',   color: 'text-orange-600 dark:text-orange-400' };
  return                { label: 'Investigate immediately',         color: 'text-rose-600 dark:text-rose-400' };
}

export default function DriverPerformancePage() {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const isAdmin = user?.role === 'super_admin' || user?.role === 'admin';
  const [period, setPeriod] = useState<Period>('last30');
  const [perf, setPerf] = useState<Performance | null>(null);
  const [worst, setWorst] = useState<WorstTrip[]>([]);
  const [loading, setLoading] = useState(true);
  const [recomputing, setRecomputing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        apiClient.get<Performance>('/api/v1/driver-scoring/drivers/' + id + '/performance?period=' + period),
        apiClient.get<WorstTrip[]>('/api/v1/driver-scoring/drivers/' + id + '/performance/worst-trips?period=' + period),
      ]);
      setPerf(r1.data);
      setWorst(r2.data);
      setError(null);
    } catch (err) {
      setError(extractErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, [id, period]);

  useEffect(() => { refresh(); }, [refresh]);

  async function recompute() {
    if (!id) return;
    setRecomputing(true);
    try {
      await apiClient.post('/api/v1/driver-scoring/drivers/' + id + '/performance/recompute?period=' + period);
      await refresh();
    } catch (err) {
      window.alert('Recompute failed: ' + extractErrorMessage(err));
    } finally {
      setRecomputing(false);
    }
  }

  if (loading && !perf) return (
    <div className="min-h-screen bg-background flex items-center justify-center">
      <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
    </div>
  );

  if (error || !perf) return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center gap-4 p-6">
      <AlertTriangle className="h-8 w-8 text-amber-500" />
      <div className="text-sm text-muted-foreground text-center">{error ?? 'No data'}</div>
      <Link to="/dashboard/fuel/driver-leaderboard"><Button variant="outline">Back to leaderboard</Button></Link>
    </div>
  );

  const t = tier(perf.compositeScore);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="border-b border-border bg-card sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between gap-2">
          <Link to="/dashboard/fuel/driver-leaderboard">
            <Button variant="ghost" size="sm" className="gap-2"><ArrowLeft className="h-4 w-4" /> Leaderboard</Button>
          </Link>
          <div className="text-sm font-medium truncate">{perf.driverName ?? perf.driverId.slice(0, 8)}</div>
          {isAdmin ? (
            <Button onClick={recompute} disabled={recomputing} variant="ghost" size="sm" className="gap-1.5 h-8">
              {recomputing ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCcw className="h-3.5 w-3.5" />}
              Recompute
            </Button>
          ) : <div className="w-24" />}
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-4 space-y-4">
        <div className="flex flex-wrap gap-2">
          {(['last7', 'last30', 'last90'] as const).map((p) => (
            <button key={p} onClick={() => setPeriod(p)}
              className={'px-3 py-1.5 rounded-md text-xs ' + (period === p ? 'bg-foreground text-background' : 'bg-muted hover:bg-muted/70')}>
              {p.replace('last', 'Last ') + 'd'}
            </button>
          ))}
        </div>

        {/* Composite gauge */}
        <div className="rounded-lg border border-border p-6 text-center space-y-2">
          <div className="text-xs uppercase tracking-wider text-muted-foreground">Composite score</div>
          <div className={'text-6xl font-display ' + t.color}>{perf.compositeScore !== null ? perf.compositeScore.toFixed(0) : '—'}</div>
          <div className={'text-sm ' + t.color}>{t.label}</div>
          <div className="text-xs text-muted-foreground pt-2">
            {perf.totalTrips} trips · {perf.totalKm.toFixed(0)} km · {perf.periodStart} → {perf.periodEnd}
          </div>
          {perf.wasteCostSar > 0 && (
            <div className="text-sm text-amber-700 dark:text-amber-400 inline-flex items-center gap-1.5 pt-1">
              <TrendingDown className="h-3.5 w-3.5" /> Cost to company: <span className="font-mono">{fmtSar(perf.wasteCostSar)}</span>
            </div>
          )}
        </div>

        {/* Sub-scores */}
        <div className="space-y-2">
          <SubScoreBar label="Consumption efficiency" weight="40%" score={perf.scoreConsumption} evidence={perf.evidence?.consumption} />
          <SubScoreBar label="Idle discipline" weight="25%" score={perf.scoreIdle} evidence={perf.evidence?.idle} />
          <SubScoreBar label="Driving style" weight="15%" score={perf.scoreDrivingStyle} evidence={perf.evidence?.drivingStyle} />
          <SubScoreBar label="Schedule adherence" weight="10%" score={perf.scoreSchedule} evidence={perf.evidence?.schedule} />
          <SubScoreBar label="Maintenance flags" weight="10%" score={perf.scoreMaintenance} evidence={perf.evidence?.maintenance} />
        </div>

        {/* Worst trips */}
        <div className="rounded-lg border border-border">
          <div className="px-4 py-2 bg-muted/40 text-xs uppercase tracking-wider text-muted-foreground">Trips that hurt the score most</div>
          {worst.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">No high-cost trips in this period.</div>
          ) : (
            <div className="divide-y divide-border">
              {worst.map((t) => (
                <div key={t.id} className="px-4 py-2 flex items-center justify-between gap-2 text-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    <span className="font-mono text-xs text-muted-foreground">{fmtDate(t.trip_date)}</span>
                    <span className="font-mono text-xs">{t.code}</span>
                    <span className="text-xs text-muted-foreground truncate">{t.vehicle_plate ?? ''}</span>
                  </div>
                  <div className="flex items-center gap-3 text-xs">
                    {t.consumption_variance_pct !== null && (
                      <span className="text-muted-foreground">+{Number(t.consumption_variance_pct).toFixed(0)}%</span>
                    )}
                    <span className="font-mono">{fmtSar(Number(t.waste_cost_sar ?? 0))}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}

function SubScoreBar({ label, weight, score, evidence }: { label: string; weight: string; score: number | null; evidence?: unknown }) {
  const ev = (evidence ?? {}) as Record<string, unknown>;
  const color =
    score === null ? 'bg-muted' :
    score >= 75 ? 'bg-emerald-500' :
    score >= 60 ? 'bg-amber-500' :
    score >= 40 ? 'bg-orange-500' :
                  'bg-rose-500';
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-sm font-medium">{label} <span className="text-xs text-muted-foreground ms-1">{weight}</span></div>
        <div className="font-mono text-sm">{score !== null ? score.toFixed(0) : '—'}</div>
      </div>
      <div className="h-2 bg-muted rounded overflow-hidden">
        <div className={'h-full ' + color} style={{ width: (score ?? 0) + '%' }} />
      </div>
      {Object.keys(ev).length > 0 && (
        <div className="text-[11px] text-muted-foreground mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
          {Object.entries(ev).map(([k, v]) => (
            <span key={k}>{k}: <span className="font-mono">{typeof v === 'number' ? (Math.round(v * 100) / 100) : String(v)}</span></span>
          ))}
        </div>
      )}
    </div>
  );
}
